import {
  applyCashTenderKey,
  resolveCashTenderSuggestions,
  type CashTenderKey,
} from "../payment/cash-tender-suggestion-resolver";

const CASH_MODAL_SELECTOR = ".cash-sale-modal";
const CASH_INPUT_SELECTOR = ".cash-sale-modal__field input[type='number']";
const PAYMENT_METHOD_SELECTOR = ".cash-sale-modal__method";
const SELECTED_PAYMENT_METHOD_SELECTOR = ".cash-sale-modal__method--selected";
const TENDER_SURFACE_CLASS = "cash-sale-modal__tender-tools";
const TENDER_ENHANCED_ATTRIBUTE = "data-cash-tender-enhanced";
const TENDER_INITIALIZED_ATTRIBUTE = "data-cash-tender-initialized";
const TENDER_ENTRY_MODE_ATTRIBUTE = "data-cash-tender-entry-mode";
const TENDER_LANDSCAPE_ATTRIBUTE = "data-cash-tender-landscape";
const PAYMENT_FASTPATH_ATTRIBUTE = "data-payment-fastpath-initialized";
const PAYMENT_SURFACE_ATTRIBUTE = "data-payment-surface";

function precisionFromInput(input: HTMLInputElement) {
  const step = input.step;
  if (!step || step === "any") return 2;
  const decimal = step.indexOf(".");
  return decimal < 0 ? 0 : Math.min(6, step.length - decimal - 1);
}

function setControlledInputValue(input: HTMLInputElement, value: string) {
  const valueSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value")?.set;
  if (valueSetter) valueSetter.call(input, value);
  else input.value = value;

  input.dispatchEvent(new Event("input", { bubbles: true }));
  input.dispatchEvent(new Event("change", { bubbles: true }));
  window.setTimeout(refreshCashTenderPresentation, 0);
}

function minorRounded(value: number, precision: number) {
  const factor = 10 ** precision;
  return Math.round(value * factor) / factor;
}

function isLandscapeTenderViewport() {
  return window.innerWidth >= 760 && window.innerWidth > window.innerHeight;
}

function displayPrefix(modal: HTMLElement) {
  const total = modal.querySelector<HTMLElement>(".cash-sale-modal__total strong")?.textContent?.trim() ?? "";
  const prefix = total.match(/^[^0-9\-]+/)?.[0]?.trim();
  return prefix ?? "";
}

function displayNumber(value: number, precision: number) {
  return new Intl.NumberFormat("th-TH", {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  }).format(value);
}

function displayTenderMoney(modal: HTMLElement, value: number, precision: number) {
  return `${displayPrefix(modal)}${displayNumber(value, precision)}`;
}

function createKeyButton(key: CashTenderKey, label: string, className = "") {
  const button = document.createElement("button");
  button.type = "button";
  button.className = `cash-sale-modal__tender-key ${className}`.trim();
  button.dataset.tenderKey = key;
  button.textContent = label;
  return button;
}

function clearChildren(element: HTMLElement) {
  while (element.firstChild) element.removeChild(element.firstChild);
}

/**
 * Payment method readiness/order is already resolved by CashSaleRuntimeHost from
 * the local Payment Runtime. The presentation adapter only activates the first
 * ready method card once so checkout opens directly on a usable work surface.
 * It does not create a Payment Method, choose a Provider, or commit a Payment.
 */
function activateInitialPaymentSurface(modal: HTMLElement) {
  if (modal.hasAttribute(PAYMENT_FASTPATH_ATTRIBUTE)) return;
  if (modal.querySelector(SELECTED_PAYMENT_METHOD_SELECTOR)) {
    modal.setAttribute(PAYMENT_FASTPATH_ATTRIBUTE, "true");
    return;
  }

  const methods = Array.from(modal.querySelectorAll<HTMLButtonElement>(PAYMENT_METHOD_SELECTOR));
  const firstReadyMethod = methods.find((method) => !method.disabled);
  if (!firstReadyMethod) return;

  modal.setAttribute(PAYMENT_FASTPATH_ATTRIBUTE, "true");
  firstReadyMethod.click();
}

function syncPaymentSurfaceState(modal: HTMLElement) {
  const cashInput = modal.querySelector<HTMLInputElement>(CASH_INPUT_SELECTOR);
  const selectedMethod = modal.querySelector(SELECTED_PAYMENT_METHOD_SELECTOR);
  if (cashInput) {
    modal.setAttribute(PAYMENT_SURFACE_ATTRIBUTE, "CASH");
  } else if (selectedMethod) {
    modal.setAttribute(PAYMENT_SURFACE_ATTRIBUTE, "METHOD");
  } else {
    modal.setAttribute(PAYMENT_SURFACE_ATTRIBUTE, "CHOICE");
  }
}

function renderTenderSurface(modal: HTMLElement, input: HTMLInputElement, surface: HTMLElement) {
  const amountDue = Number(input.min);
  if (!Number.isFinite(amountDue) || amountDue < 0) return;

  const precision = precisionFromInput(input);
  const maxSuggestions = 6;
  const suggestions = resolveCashTenderSuggestions({ amountDue, precision, maxSuggestions });
  const signature = `${maxSuggestions}|${suggestions.map((item) => `${item.kind}:${item.amount}`).join("|")}`;
  if (surface.dataset.tenderSignature === signature) return;
  surface.dataset.tenderSignature = signature;
  clearChildren(surface);

  const quickSection = document.createElement("div");
  quickSection.className = "cash-sale-modal__tender-quick";

  const quickTitle = document.createElement("div");
  quickTitle.className = "cash-sale-modal__tender-caption";
  quickTitle.textContent = "รับเงินเร็ว";
  quickSection.appendChild(quickTitle);

  const quickGrid = document.createElement("div");
  quickGrid.className = "cash-sale-modal__tender-quick-grid";

  suggestions.forEach((suggestion) => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "cash-sale-modal__tender-quick-button";
    button.dataset.tenderValue = suggestion.amount.toFixed(precision);

    const amount = document.createElement("strong");
    amount.textContent = suggestion.kind === "EXACT"
      ? `พอดี ${displayTenderMoney(modal, suggestion.amount, precision)}`
      : displayTenderMoney(modal, suggestion.amount, precision);

    const change = document.createElement("span");
    change.textContent = `ทอน ${displayTenderMoney(modal, suggestion.change, precision)}`;

    button.appendChild(amount);
    button.appendChild(change);
    quickGrid.appendChild(button);
  });

  quickSection.appendChild(quickGrid);
  surface.appendChild(quickSection);

  const keypadSection = document.createElement("div");
  keypadSection.className = "cash-sale-modal__tender-keypad-shell";

  const keypad = document.createElement("div");
  keypad.className = "cash-sale-modal__tender-keypad";
  ["7", "8", "9", "4", "5", "6", "1", "2", "3", "0", "00", "."].forEach((key) => {
    keypad.appendChild(createKeyButton(key as CashTenderKey, key));
  });

  const keypadActions = document.createElement("div");
  keypadActions.className = "cash-sale-modal__tender-keypad-actions";
  keypadActions.appendChild(createKeyButton("BACKSPACE", "⌫", "cash-sale-modal__tender-key--utility"));
  keypadActions.appendChild(createKeyButton("CLEAR", "ล้าง", "cash-sale-modal__tender-key--utility cash-sale-modal__tender-key--clear"));

  keypadSection.appendChild(keypad);
  keypadSection.appendChild(keypadActions);
  surface.appendChild(keypadSection);
}

function refreshConfirmSummary(modal: HTMLElement, input: HTMLInputElement) {
  const amountDue = Number(input.min);
  const tendered = Number(input.value);
  const precision = precisionFromInput(input);
  const primary = modal.querySelector<HTMLButtonElement>(".cash-sale-modal__actions .cash-sale-modal__primary");
  if (!primary || primary.disabled || !Number.isFinite(amountDue) || !Number.isFinite(tendered) || tendered < amountDue) return;

  const change = minorRounded(tendered - amountDue, precision);
  const label = `รับ ${displayTenderMoney(modal, tendered, precision)} • ทอน ${displayTenderMoney(modal, change, precision)}`;
  if (primary.textContent !== label) primary.textContent = label;
  primary.dataset.cashTenderConfirm = "true";
}

function enhanceCashTenderModal(modal: HTMLElement) {
  const input = modal.querySelector<HTMLInputElement>(CASH_INPUT_SELECTOR);
  if (!input) {
    modal.removeAttribute(TENDER_ENHANCED_ATTRIBUTE);
    modal.removeAttribute(TENDER_INITIALIZED_ATTRIBUTE);
    modal.removeAttribute(TENDER_ENTRY_MODE_ATTRIBUTE);
    modal.removeAttribute(TENDER_LANDSCAPE_ATTRIBUTE);
    return;
  }

  modal.setAttribute(TENDER_ENHANCED_ATTRIBUTE, "true");
  modal.setAttribute(TENDER_LANDSCAPE_ATTRIBUTE, isLandscapeTenderViewport() ? "true" : "false");
  input.readOnly = true;
  input.tabIndex = -1;
  input.autocomplete = "off";
  input.removeAttribute("autofocus");
  input.setAttribute("inputmode", "none");
  input.setAttribute("aria-label", "จำนวนเงินสดที่รับ");

  if (!modal.hasAttribute(TENDER_INITIALIZED_ATTRIBUTE)) {
    modal.setAttribute(TENDER_INITIALIZED_ATTRIBUTE, "true");
    modal.setAttribute(TENDER_ENTRY_MODE_ATTRIBUTE, "DEFAULT_EXACT");
  }

  let surface = modal.querySelector<HTMLElement>(`.${TENDER_SURFACE_CLASS}`);
  if (!surface) {
    surface = document.createElement("div");
    surface.className = TENDER_SURFACE_CLASS;
    const field = input.closest(".cash-sale-modal__field");
    if (field?.parentNode) field.parentNode.insertBefore(surface, field.nextSibling);

    surface.addEventListener("click", (event) => {
      const target = event.target instanceof Element ? event.target.closest<HTMLButtonElement>("button") : null;
      if (!target) return;

      const currentInput = modal.querySelector<HTMLInputElement>(CASH_INPUT_SELECTOR);
      if (!currentInput) return;

      const explicitValue = target.dataset.tenderValue;
      if (explicitValue != null) {
        modal.setAttribute(TENDER_ENTRY_MODE_ATTRIBUTE, "USER_VALUE");
        setControlledInputValue(currentInput, explicitValue);
        return;
      }

      const key = target.dataset.tenderKey as CashTenderKey | undefined;
      if (!key) return;

      const replaceDefaultExact = modal.getAttribute(TENDER_ENTRY_MODE_ATTRIBUTE) === "DEFAULT_EXACT"
        && key !== "BACKSPACE"
        && key !== "CLEAR";
      modal.setAttribute(TENDER_ENTRY_MODE_ATTRIBUTE, "USER_VALUE");

      const nextValue = applyCashTenderKey(
        replaceDefaultExact ? "" : currentInput.value,
        key,
        precisionFromInput(currentInput),
      );
      setControlledInputValue(currentInput, nextValue);
    });
  }

  renderTenderSurface(modal, input, surface);
  refreshConfirmSummary(modal, input);
}

function refreshCashTenderPresentation() {
  document.querySelectorAll<HTMLElement>(CASH_MODAL_SELECTOR).forEach((modal) => {
    activateInitialPaymentSurface(modal);
    syncPaymentSurfaceState(modal);
    enhanceCashTenderModal(modal);
    syncPaymentSurfaceState(modal);
  });
}

function blockSystemKeyboard(event: Event) {
  const target = event.target;
  if (!(target instanceof HTMLInputElement) || !target.matches(CASH_INPUT_SELECTOR)) return;
  target.readOnly = true;
  target.setAttribute("inputmode", "none");
  target.blur();
}

/**
 * Presentation adapter over CashSaleRuntimeHost's existing local Payment Runtime
 * and controlled tender state.
 *
 * It removes the redundant method-selection step when a ready method is already
 * available, replaces normal Cash touch entry with the app-owned keypad/quick
 * tender surface, and blocks OS soft-keyboard focus. Payment readiness, amount,
 * validation, Local Commit, immutable tender/change snapshot, Outbox and
 * Canonical reconciliation remain owned by the existing Payment/Commerce runtime.
 */
export function registerCashTenderInputAdapter() {
  if (typeof document === "undefined" || !document.body || typeof MutationObserver === "undefined") {
    return () => undefined;
  }

  document.addEventListener("focus", blockSystemKeyboard, true);
  document.addEventListener("mousedown", blockSystemKeyboard, true);
  document.addEventListener("touchstart", blockSystemKeyboard, true);
  window.addEventListener("resize", refreshCashTenderPresentation);
  window.addEventListener("orientationchange", refreshCashTenderPresentation);

  const observer = new MutationObserver(refreshCashTenderPresentation);
  observer.observe(document.body, { childList: true, subtree: true, characterData: true });
  refreshCashTenderPresentation();

  return () => {
    observer.disconnect();
    document.removeEventListener("focus", blockSystemKeyboard, true);
    document.removeEventListener("mousedown", blockSystemKeyboard, true);
    document.removeEventListener("touchstart", blockSystemKeyboard, true);
    window.removeEventListener("resize", refreshCashTenderPresentation);
    window.removeEventListener("orientationchange", refreshCashTenderPresentation);
  };
}
